import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import type { Essential } from "../api/types";
import { Icon } from "./icons";
import { toast } from "./toast";
import { backdropClose } from "../lib/backdrop";

/**
 * Upload to Frame — the pickups laid out as comments for the review files on
 * Frame.io.
 *
 * Frame.io's share viewer has no import, so Claude posts the comments in its
 * browser with the frame-comment-upload skill, and checks every one against the
 * plan afterwards. This dialog answers, once, everything that run used to stop
 * and ask (the run-up, "No issues" flags, blank comments, addressed pickups,
 * which files) and makes a plan the skill reads through a link with no login.
 *
 * The review link comes from Project Essentials. Its password stays here: Claude
 * doesn't type passwords, so the person pastes it into Frame.io themselves.
 */

type Choice<T extends string | number> = { v: T; label: string };

const PRE_ROLL: Choice<number>[] = [
  { v: 0, label: "On time" },
  { v: 1, label: "1 s early" },
  { v: 2, label: "2 s early" },
  { v: 3, label: "3 s early" },
];
const NO_ISSUES: Choice<"post" | "skip">[] = [{ v: "post", label: "Post them" }, { v: "skip", label: "Leave out" }];
const BLANK: Choice<"title" | "skip">[] = [{ v: "title", label: "Use the title" }, { v: "skip", label: "Leave out" }];
const ADDRESSED: Choice<"include" | "skip">[] = [{ v: "include", label: "Include" }, { v: "skip", label: "Leave out" }];

type Skip = "no_issues" | "blank" | "addressed" | "no_time" | "not_selected";
const SKIPPED: Record<Exclude<Skip, "not_selected">, [string, string]> = {
  no_issues: ["“No issues” flag", "“No issues” flags"],
  blank: ["pickup with no comment", "pickups with no comment"],
  addressed: ["addressed pickup", "addressed pickups"],
  no_time: ["pickup with no time", "pickups with no time"],
};

interface Available { chapter_id: string; filename: string; pickups: number; comments: number }
interface Summary {
  counts: { comments: number; files: number; skipped: Record<Skip, number> };
  frame: { review_link: string; has_password: boolean; folder: string };
  warnings: string[];
  available: Available[];
}
interface Made { token: string; path: string; expires_at: string; summary: Summary }

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function folderKey(projectId: string) {
  return `mb.frameFolder.${projectId}`;
}
function loadFolder(projectId: string): string {
  try { return localStorage.getItem(folderKey(projectId)) || ""; } catch { return ""; }
}
function saveFolder(projectId: string, folder: string) {
  try { localStorage.setItem(folderKey(projectId), folder); } catch { /* storage blocked */ }
}

async function copyText(text: string, done: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast(done, "success");
  } catch {
    toast("Couldn't copy — your browser blocked the clipboard", "error");
  }
}

function Seg<T extends string | number>({ value, options, onChange, label }: {
  value: T; options: Choice<T>[]; onChange: (v: T) => void; label: string;
}) {
  return (
    <span className="fud-seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={String(o.v)} type="button" className={value === o.v ? "on" : ""} aria-pressed={value === o.v}
          onClick={() => onChange(o.v)}>
          {o.label}
        </button>
      ))}
    </span>
  );
}

export function FrameUploadDialog({ projectId, title, essentials, onClose }: {
  projectId: string; title: string; essentials: Essential[]; onClose: () => void;
}) {
  const link = essentials.find((e) => e.id === "review_link")?.value.url?.trim() || "";
  const password = essentials.find((e) => e.id === "review_pw")?.value.pw || "";
  const [preRoll, setPreRoll] = useState(1);
  const [noIssues, setNoIssues] = useState<"post" | "skip">("post");
  const [blank, setBlank] = useState<"title" | "skip">("title");
  const [addressed, setAddressed] = useState<"include" | "skip">("include");
  const [folder, setFolder] = useState(() => loadFolder(projectId));
  // null = every file with pickups; a list once the person unticks one.
  const [picked, setPicked] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState<Made | null>(null);

  const base = `/api/modules/proofing/projects/${projectId}/frame-plan`;
  const options = useMemo(() => ({
    pre_roll_s: preRoll, no_issues: noIssues, blank, addressed,
    ...(picked ? { chapter_ids: picked } : {}),
  }), [preRoll, noIssues, blank, addressed, picked]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["frame-plan", projectId, options],
    queryFn: () => api<Summary>(base, { method: "POST", body: JSON.stringify({ ...options, preview: true }) }),
    placeholderData: keepPreviousData,
    staleTime: 0,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const files = data?.available ?? [];
  const chosen = new Set(picked ?? files.map((f) => f.chapter_id));
  const counts = data?.counts;
  const left = counts
    ? (Object.keys(SKIPPED) as Array<keyof typeof SKIPPED>)
        .map((k) => ({ k, n: counts.skipped[k] || 0 }))
        .filter((s) => s.n > 0)
    : [];

  function toggle(cid: string) {
    const next = new Set(chosen);
    if (next.has(cid)) next.delete(cid); else next.add(cid);
    setPicked(next.size === files.length ? null : files.map((f) => f.chapter_id).filter((id) => next.has(id)));
  }

  async function make() {
    setBusy(true);
    try {
      const r = await api<Made>(base, { method: "POST", body: JSON.stringify({ ...options, folder: folder.trim() }) });
      saveFolder(projectId, folder.trim());
      setMade(r);
    } catch (e: any) {
      toast(e?.message || "Couldn't make the plan — please try again.", "error");
    } finally {
      setBusy(false);
    }
  }

  const planUrl = made ? `${window.location.origin}${made.path}` : "";
  // The task it starts is renamed first, so every book's upload reads the same in Claude's task list
  // (the skill builds the same name from the plan when the line is missing).
  const request = made
    ? `Upload the pickups for “${title}” to Frame.io with the frame-comment-upload skill.\n`
      + `Before anything else, rename this task to: "${title}" - notes upload to Frame\n`
      + `Plan: ${planUrl}`
    : "";
  const expires = made ? new Date(made.expires_at).toLocaleDateString(undefined, { day: "numeric", month: "long" }) : "";

  return (
    <div className="modal-overlay" {...backdropClose(() => { if (!busy) onClose(); })}>
      <div className="modal-card fud" role="dialog" aria-modal="true" aria-labelledby="fud-title"
        onClick={(e) => e.stopPropagation()}>
        <h3 id="fud-title">Upload to Frame</h3>

        {made ? (
          <>
            <p className="fud-lede">
              The plan is ready: <b>{plural(made.summary.counts.comments, "comment")}</b> on{" "}
              <b>{plural(made.summary.counts.files, "file")}</b>. Claude posts them in its browser and checks every
              one against the plan when it's done.
            </p>
            <ol className="fud-steps">
              <li>
                <div className="fud-step-head">
                  <span>Paste this into Claude (the desktop app, with its browser pane)</span>
                  <button className="btn sm" onClick={() => copyText(request, "Request copied")}>
                    <Icon name="copy" size={14} /> Copy
                  </button>
                </div>
                <pre className="fud-request">{request}</pre>
              </li>
              <li>
                <div className="fud-step-head">
                  <span>
                    {password
                      ? "When Frame.io asks for the review password, paste it in yourself — Claude doesn't type passwords."
                      : "If Frame.io asks for a password, type it in yourself — Claude doesn't type passwords."}
                  </span>
                  {password && (
                    <button className="btn sm" onClick={() => copyText(password, "Password copied")}>
                      <Icon name="copy" size={14} /> Copy password
                    </button>
                  )}
                </div>
              </li>
              <li>
                <span>Keep Claude's browser pane showing while it posts — a hidden pane stops the audio loading.</span>
              </li>
            </ol>
            <p className="fud-foot">
              The link works until {expires} and shows only this plan.{" "}
              <a href={`${made.path}?download=1`}>Download the plan</a> ·{" "}
              <button className="linklike" onClick={() => copyText(planUrl, "Plan link copied")}>Copy the link</button>
            </p>
            <div className="modal-actions">
              <button className="btn primary" onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <>
            <p className="fud-lede">
              Lays the pickups out as comments for the review files on Frame.io, with the words the exports carry.
              Claude then posts them for you in its browser and checks each one.
            </p>

            <div className="fud-where">
              <div className="fud-where-row">
                <span className="fud-k">Review link</span>
                {link ? (
                  <a className="fud-v mono" href={link} target="_blank" rel="noreferrer noopener" title={link}>
                    {link.replace(/^https?:\/\//, "")} <Icon name="external" size={12} />
                  </a>
                ) : (
                  <span className="fud-v fud-missing">Not in Project Essentials yet</span>
                )}
              </div>
              <div className="fud-where-row">
                <span className="fud-k">Password</span>
                <span className="fud-v">
                  {password ? "In Project Essentials — you'll paste it in yourself" : "None saved"}
                </span>
              </div>
              <label className="fud-where-row">
                <span className="fud-k">Folder</span>
                <input className="input fud-folder" value={folder} maxLength={200} placeholder="e.g. 2nd Review Files"
                  onChange={(e) => setFolder(e.target.value)} />
              </label>
            </div>

            <div className="fud-opts">
              <div className="fud-opt">
                <span className="fud-k">Post each comment</span>
                <Seg value={preRoll} options={PRE_ROLL} onChange={setPreRoll} label="When each comment is posted" />
              </div>
              <div className="fud-opt">
                <span className="fud-k">“No issues” flags</span>
                <Seg value={noIssues} options={NO_ISSUES} onChange={setNoIssues} label="No issues flags" />
              </div>
              <div className="fud-opt">
                <span className="fud-k">Pickups with no comment</span>
                <Seg value={blank} options={BLANK} onChange={setBlank} label="Pickups with no comment" />
              </div>
              <div className="fud-opt">
                <span className="fud-k">Already addressed</span>
                <Seg value={addressed} options={ADDRESSED} onChange={setAddressed} label="Addressed pickups" />
              </div>
            </div>

            {isLoading ? (
              <div className="nid-wait"><span className="spinner" style={{ width: 15, height: 15 }} /> Reading the pickups…</div>
            ) : error ? (
              <p className="dfd-warn"><Icon name="alert" size={14} /> Couldn't read the pickups. Close this and try again.</p>
            ) : files.length === 0 ? (
              <p className="nid-none">There are no pickups to post yet.</p>
            ) : (
              <>
                <div className="fud-files-head">
                  <span className="fud-k">Files</span>
                  <button className="linklike" onClick={() => setPicked(picked === null ? [] : null)}>
                    {picked === null ? "Untick all" : "Tick all"}
                  </button>
                </div>
                <div className="dfd-list nid-list fud-files">
                  {files.map((f) => (
                    <label key={f.chapter_id} className="dfd-row fud-file">
                      <span className="fud-file-name">
                        <input type="checkbox" checked={chosen.has(f.chapter_id)} onChange={() => toggle(f.chapter_id)} />
                        <span className="dfd-name mono">{f.filename}</span>
                      </span>
                      <span className="dfd-note">
                        {chosen.has(f.chapter_id) ? plural(f.comments, "comment") : "left out"}
                      </span>
                    </label>
                  ))}
                </div>
                <p className={`fud-total${isFetching ? " stale" : ""}`}>
                  <b>{plural(counts?.comments ?? 0, "comment")}</b> on <b>{plural(counts?.files ?? 0, "file")}</b>
                  {preRoll > 0 && <>, each {preRoll} s before its pickup</>}, to the whole second (Frame.io takes no
                  finer).
                </p>
                {left.length > 0 && (
                  <p className="nid-skipped">
                    Left out:{" "}
                    {left.map((s, i) => (
                      <span key={s.k}>{i > 0 && " · "}<b>{s.n}</b> {s.n === 1 ? SKIPPED[s.k][0] : SKIPPED[s.k][1]}</span>
                    ))}
                  </p>
                )}
                {(data?.warnings ?? []).map((w) => (
                  <p key={w} className="dfd-warn"><Icon name="alert" size={14} /> {w}</p>
                ))}
              </>
            )}

            {!link && (
              <p className="dfd-warn">
                <Icon name="alert" size={14} /> Add the Online Review Link to Project Essentials first — it's how Claude
                finds the files on Frame.io.
              </p>
            )}

            <div className="modal-actions">
              <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="btn primary" onClick={make}
                disabled={busy || isLoading || isFetching || !link || !(counts?.comments)}>
                {busy ? "Making the plan…" : "Make the upload plan"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
